import {
  ChartConfiguration,
  ChartDataSource,
  ChartFilter,
  ChartType,
  CreateChartConfigurationRequest,
  FunnelMode,
  FunnelStage,
  FunnelStyle,
  PieMode,
  PlatformMetricCatalogEntry,
  SortDirection,
  TargetMode,
  TimeframePreset,
  UpdateChartConfigurationRequest,
  XInterval,
  YAgg,
} from "@/types/breeze/ChartConfiguration";
import { BaseService } from "../BaseService";

interface PlatformMetricCatalogWire {
  id: string;
  label: string;
  shape: string;
  supported_chart_types: string[];
  requires_period: boolean;
  stack_by_options?: { id: string; label: string; values?: string[] }[];
}

interface ChartConfigurationResponse {
  id: number;
  name: string;
  data_source: string;
  chart_type: string;
  entity_name: string | null;
  x_field: string | null;
  y_field: string | null;
  pie_mode: string | null;
  platform_metric: string | null;
  platform_period: string | null;
  stack_by: string | null;
  quota_metric: string | null;
  y_agg: string | null;
  x_interval: string | null;
  timeframe_preset: string | null;
  timeframe_from: string | null;
  timeframe_to: string | null;
  timeframe_offset_days: number | null;
  compare_enabled: boolean | null;
  compare_previous_days: number | null;
  compare_show_overlay: boolean | null;
  limit: number | null;
  sort_direction: string | null;
  cumulative: boolean | null;
  target: number | null;
  target_mode: string | null;
  filters: string | null;
  funnel_stages: string | null;
  funnel_style: string | null;
  funnel_mode: string | null;
  cohort_field: string | null;
  config: string | null;
  tenant_id?: number;
  created_at?: string;
  updated_at?: string;
}

export class ChartConfigurationService extends BaseService {
  public async getChartConfigurations(
    entityName?: string,
  ): Promise<ChartConfiguration[]> {
    const query = entityName
      ? `?entityName=${encodeURIComponent(entityName)}`
      : "";
    const response = await this.getRequest<ChartConfigurationResponse[]>(
      `/charts${query}`,
    );
    return response.map(this.mapToChartConfiguration);
  }

  public async getChartConfiguration(id: number): Promise<ChartConfiguration> {
    const response = await this.getRequest<ChartConfigurationResponse>(
      `/charts/${id}`,
    );
    return this.mapToChartConfiguration(response);
  }

  public async createChartConfiguration(
    request: CreateChartConfigurationRequest,
  ): Promise<ChartConfiguration> {
    const response = await this.postRequest<
      CreateChartConfigurationRequest,
      ChartConfigurationResponse
    >("/charts", request, "Chart configuration saved");

    return this.mapToChartConfiguration(response);
  }

  public async updateChartConfiguration(
    id: number,
    request: UpdateChartConfigurationRequest,
  ): Promise<ChartConfiguration> {
    const response = await this.putRequest<
      UpdateChartConfigurationRequest,
      ChartConfigurationResponse
    >(`/charts/${id}`, request, "Chart configuration updated");

    return this.mapToChartConfiguration(response);
  }

  public async deleteChartConfiguration(id: number): Promise<void> {
    await this.deleteRequest(`/charts/${id}`, "Chart configuration deleted");
  }

  public async previewChartData(entityName: string): Promise<{
    items: Record<string, unknown>[];
    total_items: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
  }> {
    return this.postRequest(
      "/charts/preview",
      { entityName },
      undefined,
      true,
      true,
    );
  }

  public async previewChartConfig(
    request: CreateChartConfigurationRequest,
  ): Promise<unknown> {
    return this.postRequest(
      "/charts/preview-config",
      request,
      undefined,
      true,
      true,
      true,
    );
  }

  public async getPlatformMetrics(): Promise<PlatformMetricCatalogEntry[]> {
    const wire = await this.getRequest<PlatformMetricCatalogWire[]>("/charts/platform-metrics");
    return wire.map((m) => ({
      id: m.id,
      label: m.label,
      shape: m.shape as PlatformMetricCatalogEntry["shape"],
      supportedChartTypes: m.supported_chart_types as PlatformMetricCatalogEntry["supportedChartTypes"],
      requiresPeriod: m.requires_period,
      stackByOptions: m.stack_by_options ?? [],
    }));
  }

  private mapToChartConfiguration(
    item: ChartConfigurationResponse,
  ): ChartConfiguration {
    return {
      id: item.id,
      name: item.name,
      dataSource: (item.data_source || "entity") as ChartDataSource,
      chartType: item.chart_type as ChartType,
      entityName: item.entity_name,
      xField: item.x_field,
      yField: item.y_field,
      pieMode: item.pie_mode as PieMode | null,
      platformMetric: item.platform_metric,
      platformPeriod: item.platform_period,
      stackBy: item.stack_by,
      quotaMetric: item.quota_metric,
      yAgg: item.y_agg as YAgg | null,
      xInterval: item.x_interval as XInterval | null,
      timeframePreset: item.timeframe_preset as TimeframePreset | null,
      timeframeFrom: item.timeframe_from,
      timeframeTo: item.timeframe_to,
      timeframeOffsetDays: item.timeframe_offset_days,
      compareEnabled: item.compare_enabled,
      comparePreviousDays: item.compare_previous_days,
      compareShowOverlay: item.compare_show_overlay,
      limit: item.limit,
      sortDirection: item.sort_direction as SortDirection | null,
      cumulative: item.cumulative,
      target: item.target,
      targetMode: item.target_mode as TargetMode | null,
      filters: parseFilters(item.filters),
      funnelStages: parseFunnelStages(item.funnel_stages),
      funnelStyle: (item.funnel_style ?? null) as FunnelStyle | null,
      funnelMode: (item.funnel_mode ?? null) as FunnelMode | null,
      cohortField: item.cohort_field ?? null,
      config: item.config || undefined,
      tenantId: item.tenant_id,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    };
  }
}

function parseFilters(raw: string | null): ChartFilter[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ChartFilter[]) : null;
  } catch (e) {
    console.warn("Corrupt chart.filters JSON, discarding:", e);
    return null;
  }
}

function parseFunnelStages(raw: string | null): FunnelStage[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FunnelStage[]) : null;
  } catch (e) {
    console.warn("Corrupt chart.funnel_stages JSON, discarding:", e);
    return null;
  }
}

export const chartConfigurationService = new ChartConfigurationService();
